import { isB2BLandingPath } from "@/lib/landing/b2b-landing-paths"
import { isReferralLandingPath } from "@/lib/landing/referral-landing-paths"
import { isSimulatorLandingPath } from "@/lib/landing/simulator-landing-paths"
import { isPartnerLandingPath } from "@/lib/partners/partner-path"

export type SiteNavSection = {
  name: string
  sectionId: string
}

export type SiteNavRoute = {
  name: string
  href: string
}

export type SiteNavItem = SiteNavSection | SiteNavRoute

export const HEADER_NAV_ITEMS: SiteNavItem[] = [
  { name: "Sobre", href: "/sobre" },
  { name: "Simular", href: "/simular" },
  { name: "Como Funciona", href: "/como-funciona" },
  { name: "Indique e Ganhe", href: "/indique-e-ganhe" },
  { name: "FAQ", href: "/faq" },
  { name: "Blog", href: "/blog" },
]

export const FOOTER_LINK_ITEMS: SiteNavItem[] = [
  { name: "Sobre", href: "/sobre" },
  { name: "Simular", href: "/simular" },
  { name: "Empresas", href: "/empresas" },
  { name: "Como Funciona", href: "/como-funciona" },
  { name: "Indique e Ganhe", href: "/indique-e-ganhe" },
  { name: "Central de Ajuda", href: "/faq" },
  { name: "Blog", href: "/blog" },
]

/** Páginas de LP com as mesmas âncoras de seção da home (home, `/simular`, `/empresas`, `/parcerias/{slug}`). */
export function hasLandingSectionAnchors(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  if (pathname === "/") return true
  if (isSimulatorLandingPath(pathname)) return true
  if (isB2BLandingPath(pathname)) return true
  return isPartnerLandingPath(pathname)
}

function isBlogPath(pathname: string): boolean {
  return pathname === "/blog" || pathname.startsWith("/blog/")
}

function isFaqPath(pathname: string): boolean {
  return pathname === "/faq" || pathname.startsWith("/faq/")
}

function isInstitutionalPath(pathname: string): boolean {
  return pathname === "/sobre" || pathname === "/como-funciona"
}

/** Logo azul (variante dark) em todas as posições do header — home, LPs, blog e FAQ. */
export function hasAlwaysDarkLogo(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  if (hasLandingSectionAnchors(pathname)) return true
  if (isReferralLandingPath(pathname)) return true
  return isBlogPath(pathname) || isFaqPath(pathname) || isInstitutionalPath(pathname)
}

/** Header transparente sobre hero escuro (home, parceiros, eventos, blog, FAQ, LP Indique e Ganhe). */
export function hasTransparentHeader(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  if (hasLandingSectionAnchors(pathname)) return true
  if (isReferralLandingPath(pathname)) return true
  return isBlogPath(pathname) || isFaqPath(pathname) || isInstitutionalPath(pathname)
}

export function isSiteNavSection(item: SiteNavItem): item is SiteNavSection {
  return "sectionId" in item
}

/** Seções com âncora local na LP Indique e Ganhe (demais itens do menu apontam para a home). */
const REFERRAL_LANDING_SECTION_IDS = new Set(["como-funciona", "faq"])

/** Resolve href de seção: `#id` na LP atual; `/#id` em rotas internas (blog, FAQ, etc.). */
export function resolveSectionHref(
  pathname: string | null | undefined,
  sectionId: string,
): string {
  if (hasLandingSectionAnchors(pathname)) {
    return `#${sectionId}`
  }
  if (isReferralLandingPath(pathname) && REFERRAL_LANDING_SECTION_IDS.has(sectionId)) {
    return `#${sectionId}`
  }
  return `/#${sectionId}`
}

export function resolveNavItemHref(
  pathname: string | null | undefined,
  item: SiteNavItem,
): string {
  if (isSiteNavSection(item)) {
    return resolveSectionHref(pathname, item.sectionId)
  }
  return item.href
}
